/**
 * Browser-side cache of GET /api/core/ipos/{id}/broker-reviews.
 *
 * Mainboard broker calls have no bulk endpoint, so the reviews page has to ask
 * per id -- over a hundred requests. They are worth keeping between visits:
 * broker research is published over a day, not by the minute, so an hour-old
 * answer is as good as a fresh one, and a reload or a second visit then costs
 * nothing.
 *
 * Storage can be unavailable (private windows, blocked site data) or full, and
 * every path here treats that as "no cache" rather than an error.
 */
const KEY = 'alambrix.brokerReviews.v1';
export const REVIEWS_TTL_MS = 60 * 60 * 1000;

const storage = () => {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

/** The cached payloads by id, or {} when there is nothing usable. */
export const readBrokerReviewsCache = (ttlMs = REVIEWS_TTL_MS, now = Date.now()) => {
  try {
    const raw = storage()?.getItem(KEY);
    if (!raw) return {};
    const { savedAt, byId } = JSON.parse(raw);
    if (!savedAt || now - savedAt > ttlMs || !byId || typeof byId !== 'object') return {};
    return byId;
  } catch {
    return {};
  }
};

/** Replaces the cache. Silently does nothing when storage refuses the write. */
export const writeBrokerReviewsCache = (byId, now = Date.now()) => {
  try {
    storage()?.setItem(KEY, JSON.stringify({ savedAt: now, byId }));
    return true;
  } catch {
    // Over quota or blocked -- the page works without it
    return false;
  }
};

export const clearBrokerReviewsCache = () => {
  try {
    storage()?.removeItem(KEY);
  } catch {
    /* nothing to clear */
  }
};
